/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';

import TargetStage from './target-stage';
import MathUtils from '../utils/math';

export default class SecondOrderTargetStage extends TargetStage {
  order = 2;
  @computed('filter', 'stageFp', 'stageQ', 'gain') get stepResponseSetup() {
    var filter = this.filter;
    var stageFp = this.stageFp;
    var stageQ = this.stageQ;
    var gain = this.gain;
    var result = filter.setupSecondOrderStepResponse(stageFp, stageQ, gain);
    return result;
  }

  @computed('timeAxis', 'stepResponseSetup') get stepResponseIdeal() {
    var timeAxis = this.timeAxis;
    var stepResponseSetup = this.stepResponseSetup;
    var a = stepResponseSetup.a;
    var b = stepResponseSetup.b;
    var c = stepResponseSetup.c;
    var w0 = stepResponseSetup.w0;
    var Q = stepResponseSetup.Q;

    var result = timeAxis.map((timePoint) => {
      var X = -c - a * w0 * w0 + 2 * b * w0 * Q; //X=-c-a*wo**2+2*b*wo*Q;

      var Y = -(w0 * w0) * (-1 + 4 * Q * Q); //Y=-wo**2*(-1 + 4*Q**2);

      var W = a * w0 * w0 - c; //W=a*wo**2-c;

      var Z = Math.exp((-timePoint * w0) / 2 / Q); //Z=exp(-t*wo/2/Q);

      var sqrtY = Math.sqrt(Y); //sqrt(Y)
      var tYQ = (timePoint * sqrtY) / 2 / Q; //t*sqrt(Y)/2/Q
      var sinhY = MathUtils.sinh(tYQ); //sinh(t*sqrt(Y)/2/Q)
      var coshY = MathUtils.cosh(tYQ); //cosh(t*sqrt(Y)/2/Q)
      var sqrtNegY = Math.sqrt(-Y); //sqrt(-Y)
      var w02 = w0 * w0; //w0**2
      var w03 = w02 * w0; //w0**3
      var exptw0 = Math.exp(timePoint * w0); // exp(t*wo)
      var res = null;

      if (Y > 0) {
        res = (X * sinhY * Z) / sqrtY / w0 + (c + W * coshY * Z) / (w0 * w0);
      } else if (Y < 0) {
        res =
          (X * Math.sin((timePoint * sqrtNegY) / 2 / Q) * Z) / sqrtNegY / w0 +
          (c + W * Math.cos((timePoint * sqrtNegY) / 2 / Q) * Z) / (w0 * w0);
      } else {
        res =
          -(c - c * exptw0 - a * w02 + a * timePoint * w03 - b * timePoint * w02 + c * timePoint * w0) / (w02 * exptw0);
      }

      return res;
    });

    return result;
  }

  @computed('filter', 'stageFp', 'stageQ', 'gain') get defaultImplementation() {
    var base = this; // for readability. To know which properties comes from the base class
    var filter = base.filter;
    var stageFp = base.stageFp;
    var stageQ = base.stageQ;
    var gain = base.gain;
    var implementation = filter.getSecondOrderStageImplementation(stageFp, stageQ, gain);
    return implementation;
  }

  createAmplitude(fp, Q, gain, frequencyArray) {
    return this.filter.createSecondOrderFilterAmplitude(fp, Q, gain, frequencyArray);
  }
}
